import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { useFacilityStore } from '../../../app/z-store/store';
import { getItem } from '../../../utils/localStorage';

const LevyContractReportTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const toast = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(10);
  
  // Current user
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [contractFilter, setContractFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Available contracts for filter
  const [contracts, setContracts] = useState([]);
  
  // Approval state
  const [approvalDialogVisible, setApprovalDialogVisible] = useState(false);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [comments, setComments] = useState('');
  const [approving, setApproving] = useState(false);
  
  const rowsPerPageOptions = [10, 20, 50, 100];

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Fully Paid', value: 'Fully Paid' },
    { label: 'Partially Paid', value: 'Partially Paid' },
    { label: 'Unpaid', value: 'Unpaid' }
  ];

  // Get current user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue;
        }

        if (parsedUser?.user) {
          setCurrentUser(parsedUser.user);
        }
      } catch (err) {
        console.error("Error getting current user:", err);
      }
    };

    getCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchReportData();
      fetchCurrentApproval();
    }
  }, [selectedFacilityId, selectedMonth]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Format month as YYYY-MM
      const year = selectedMonth.getFullYear();
      const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
      const yearMonth = `${year}-${month}`;

      const response = await makeRequest2(
        `/api/app/levy_management/${selectedFacilityId}/levy-contracts/monthly-summary?month=${yearMonth}&limit=1000`,
        'GET'
      );

      if (response.success) {
        const contractsData = response.data.data?.contracts || [];
        setReportData(contractsData);
        setStatistics(response.data.data?.statistics || null);
        
        // Extract unique contracts for filter dropdown
        const uniqueContracts = [
          { label: 'All Contracts', value: 'all' },
          ...Array.from(new Set(contractsData.map(c => c.contractName)))
            .map(name => ({ label: name, value: name }))
        ];
        setContracts(uniqueContracts);
      } else {
        toastify('Failed to fetch levy contract report', 'error');
        setReportData([]);
        setStatistics(null);
      }
    } catch (error) {
      toastify('Error fetching levy contract report', 'error');
      setReportData([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentApproval = async () => {
    try {
      const response = await makeRequest2(
        `/api/app/levy_management/invoice-approvals/${selectedFacilityId}/levy/current`,
        'GET'
      );

      if (response.success && response.data.data) {
        setCurrentApproval(response.data.data);
      } else {
        setCurrentApproval(null);
      }
    } catch (error) {
      console.error('Error fetching current approval:', error);
      setCurrentApproval(null);
    }
  };

  const handleApproveInvoices = async () => {
    if (!currentApproval) {
      toastify('No approval entry found', 'error');
      return;
    }

    if (!currentUser) {
      toastify('User information not available', 'error');
      return;
    }

    try {
      setApproving(true);

      const payload = {
        action: 'approve',
        userId: currentUser._id,
        userName: currentUser.fullName || `${currentUser.firstName} ${currentUser.lastName}`,
        comments: comments.trim(),
        deviceInfo: {
          deviceId: 'web-app',
          deviceType: 'web',
          ipAddress: 'browser'
        },
        priority: 8,
        delay: 0
      };

      const response = await makeRequest2(
        `/api/app/levy_management/invoice-approvals/${currentApproval._id}/action`,
        'POST',
        payload
      );

      if (response.success) {
        toastify('Invoice generation approved and triggered successfully', 'success');
        setApprovalDialogVisible(false);
        setComments('');
        
        // Refresh data
        await fetchReportData();
        await fetchCurrentApproval();
      } else {
        toastify(response.data?.error || 'Failed to approve invoice generation', 'error');
      }
    } catch (error) {
      console.error('Error approving invoice generation:', error);
      toastify('Error approving invoice generation', 'error');
    } finally {
      setApproving(false);
    }
  };

  const openApprovalDialog = () => {
    if (!currentApproval) {
      toastify('No pending approval found for this month', 'warning');
      return;
    }

    if (currentApproval.approvalStatus !== 'Pending') {
      toastify(`This approval has already been ${currentApproval.approvalStatus.toLowerCase()}`, 'warning');
      return;
    }

    setApprovalDialogVisible(true);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'KES 0.00';
    return `KES ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter data
  const filteredData = reportData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    
    // Text search
    const customerName = item.customer?.fullName?.toLowerCase() || '';
    const unitName = item.unit?.unitName?.toLowerCase() || '';
    const contractName = item.contractName?.toLowerCase() || '';
    
    const textMatch = customerName.includes(searchLower) || 
                     unitName.includes(searchLower) || 
                     contractName.includes(searchLower);
    
    // Contract filter
    const contractMatch = contractFilter === 'all' || item.contractName === contractFilter;
    
    // Status filter
    const statusMatch = statusFilter === 'all' || item.currentMonth?.paymentStatus === statusFilter;
    
    return textMatch && contractMatch && statusMatch;
  });

  // Column templates
  const customerBodyTemplate = (rowData) => {
    return (
      <div>
        <div className="fw-bold">{rowData.customer?.fullName || 'N/A'}</div>
        <small className="text-muted">{rowData.customer?.phoneNumber || ''}</small>
      </div>
    );
  };

  const unitBodyTemplate = (rowData) => {
    return (
      <div>
        <div>{rowData.unit?.unitName || 'N/A'}</div>
        <small className="text-muted">{rowData.unit?.division || ''}</small>
      </div>
    );
  };

  const currentInvoiceBodyTemplate = (rowData) => {
    const total = rowData.currentMonth?.totalInvoiced || 0;
    return (
      <div>
        <div className="fw-bold">{formatCurrency(total)}</div>
        <small className="text-muted">
          {rowData.currentMonth?.invoiceCount || 0} invoice(s)
        </small>
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.currentMonth?.paymentStatus || 'Unknown';
    const statusColors = {
      'Fully Paid': 'bg-success text-light',
      'Partially Paid': 'bg-warning text-dark',
      'Unpaid': 'bg-danger text-light'
    };
    
    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary text-light'}`}>
        {status}
      </span>
    );
  };

  const balanceBodyTemplate = (rowData) => {
    const balance = rowData.currentMonth?.totalBalance || 0;
    const bbf = rowData.balanceBroughtForward || 0;
    
    return (
      <div>
        <div className={balance > 0 ? 'text-danger fw-bold' : 'text-success'}>
          {formatCurrency(balance)}
        </div>
        {bbf !== 0 && (
          <small className="text-muted">BBF: {formatCurrency(bbf)}</small>
        )}
      </div>
    );
  };

  const nextMonthBodyTemplate = (rowData) => {
    const nextMonth = rowData.nextMonth;
    
    if (nextMonth?.expectedAmount !== undefined && nextMonth?.expectedAmount !== null) {
      const amount = nextMonth.expectedAmount;
      const hasUnpaidBalance = nextMonth.unpaidBalance > 0;
      const hasCredit = nextMonth.creditAmount > 0;
      const willBeAutoPaid = nextMonth.willBeAutoPaid;
      
      return (
        <div>
          <div className={`fw-bold ${amount > 0 ? 'text-success' : 'text-muted'}`}>
            {formatCurrency(amount)}
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {willBeAutoPaid && (
              <small className="badge bg-success">Auto-Paid</small>
            )}
            {hasUnpaidBalance && !willBeAutoPaid && (
              <small className="badge bg-warning text-dark">+Unpaid</small>
            )}
            {hasCredit && !willBeAutoPaid && (
              <small className="badge bg-info">-Credit</small>
            )}
          </div>
        </div>
      );
    }
    
    if (!nextMonth?.willGenerate) {
      return (
        <div>
          <div className="text-muted">No Invoice</div>
          <small className="text-muted">{nextMonth?.reason || ''}</small>
        </div>
      );
    }
    
    return <div className="text-muted">-</div>;
  };

  const levyInfoBodyTemplate = (rowData) => {
    return (
      <div>
        <div>{rowData.levy?.levyName || 'N/A'}</div>
        <small className="text-muted">{rowData.levy?.levyType || ''}</small>
      </div>
    );
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Customer Name',
      'Unit',
      'Contract',
      'Levy Type',
      'Current Invoice',
      'Amount Paid',
      'Balance',
      'Status',
      'Balance B/F',
      'Next Month Expected'
    ];

    const csvData = filteredData.map(item => [
      item.customer?.fullName || '',
      item.unit?.unitName || '',
      item.contractName || '',
      item.levy?.levyType || '',
      item.currentMonth?.totalInvoiced || 0,
      item.currentMonth?.totalPaid || 0,
      item.currentMonth?.totalBalance || 0,
      item.currentMonth?.paymentStatus || '',
      item.balanceBroughtForward || 0,
      item.nextMonth?.expectedAmount || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const year = selectedMonth.getFullYear();
    const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
    link.download = `levy_contract_report_${year}-${month}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toastify('Report exported successfully', 'success');
  };

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          Please select a facility to view the report.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <div className="card-body">
        {/* Statistics Cards */}
        {statistics && (
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <h6 className="card-title">Total Contracts</h6>
                  <h3>{statistics.totalContracts}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success text-white">
                <div className="card-body">
                  <h6 className="card-title">Current Month Invoiced</h6>
                  <h3>{formatCurrency(statistics.currentMonth?.totalInvoiced)}</h3>
                  <small>Paid: {formatCurrency(statistics.currentMonth?.totalPaid)}</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-danger text-white">
                <div className="card-body">
                  <h6 className="card-title">Outstanding Balance</h6>
                  <h3>{formatCurrency(statistics.currentMonth?.totalBalance)}</h3>
                  <small>Unpaid: {statistics.currentMonth?.unpaid}</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info text-white">
                <div className="card-body">
                  <h6 className="card-title">Next Month Expected</h6>
                  <h3>{formatCurrency(statistics.nextMonth?.estimatedTotal)}</h3>
                  <small>{statistics.nextMonth?.expectedToGenerate} contracts</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Status Banner */}
        {currentApproval && (
          <div className={`alert ${
            currentApproval.approvalStatus === 'Pending' ? 'alert-warning' :
            currentApproval.approvalStatus === 'Approved' ? 'alert-success' :
            'alert-danger'
          } mb-4`}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Invoice Generation Status:</strong> {currentApproval.approvalStatus}
                <br />
                <small>Period: {currentApproval.yearMonth}</small>
                {currentApproval.approvalStatus === 'Approved' && currentApproval.approvedBy && (
                  <>
                    <br />
                    <small>Approved by: {currentApproval.approvedBy.name} on {formatDate(currentApproval.approvedBy.approvalDate)}</small>
                  </>
                )}
              </div>
              {currentApproval.approvalStatus === 'Pending' && (
                <button
                  className="btn btn-warning"
                  onClick={openApprovalDialog}
                  disabled={approving}
                >
                  <i className="ti ti-check me-1"></i>
                  Approve & Generate Invoices
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Select Month</label>
            <Calendar
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.value)}
              view="month"
              dateFormat="MM/yy"
              className="w-100"
              showIcon
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="Customer, Unit, Contract..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Contract Type</label>
            <Dropdown
              value={contractFilter}
              options={contracts}
              onChange={(e) => setContractFilter(e.value)}
              className="w-100"
              placeholder="All Contracts"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Payment Status</label>
            <Dropdown
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)}
              className="w-100"
              placeholder="All Statuses"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end mb-3">
          <button
            className="btn btn-success me-2"
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
          >
            <i className="ti ti-download me-1"></i>
            Export to CSV
          </button>
          <button
            className="btn btn-primary"
            onClick={fetchReportData}
            disabled={loading}
          >
            <i className="ti ti-refresh me-1"></i>
            Refresh
          </button>
        </div>

        {/* Data Table */}
        <div className="dt-responsive table-responsive">
          <DataTable
            value={filteredData}
            emptyMessage="No levy contracts found for the selected month."
            sortMode="multiple"
            paginator
            rows={rows}
            rowsPerPageOptions={rowsPerPageOptions}
            stripedRows
            loading={loading}
            tableStyle={{ minWidth: '60rem' }}
          >
            <Column 
              field="customer.fullName" 
              header="Customer" 
              body={customerBodyTemplate}
              sortable 
              style={{ minWidth: '200px' }}
            />
            <Column 
              field="unit.unitName" 
              header="Unit" 
              body={unitBodyTemplate}
              sortable 
            />
            <Column 
              field="contractName" 
              header="Contract" 
              sortable 
            />
            <Column 
              field="levy.levyType" 
              header="Levy Type" 
              body={levyInfoBodyTemplate}
              sortable 
            />
            <Column 
              field="currentMonth.totalInvoiced" 
              header="Current Invoice" 
              body={currentInvoiceBodyTemplate}
              sortable 
            />
            <Column 
              field="currentMonth.paymentStatus" 
              header="Status" 
              body={statusBodyTemplate}
              sortable 
              style={{ width: '150px' }}
            />
            <Column 
              field="currentMonth.totalBalance" 
              header="Balance" 
              body={balanceBodyTemplate}
              sortable 
            />
            <Column 
              field="nextMonth.expectedAmount" 
              header="Next Month Expected" 
              body={nextMonthBodyTemplate}
              sortable 
            />
          </DataTable>
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog
        header="Approve Levy Invoice Generation"
        visible={approvalDialogVisible}
        onHide={() => {
          setApprovalDialogVisible(false);
          setComments('');
        }}
        className="w-50"
      >
        <div className="p-3">
          <div className="alert alert-info mb-4">
            <h6><i className="ti ti-info-circle me-2"></i>What will happen?</h6>
            <p className="mb-2">
              Approving this action will automatically trigger the generation of levy invoices for <strong>{currentApproval?.yearMonth}</strong>.
            </p>
            <p className="mb-0">
              <strong>Invoices will be generated for:</strong>
            </p>
            <ul className="mb-0">
              <li>All active levy contracts in the report</li>
              <li>Total expected amount: <strong>{formatCurrency(statistics?.nextMonth?.estimatedTotal)}</strong></li>
              <li>Number of contracts: <strong>{statistics?.nextMonth?.expectedToGenerate}</strong></li>
            </ul>
          </div>

          <div className="mb-4">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Facility:</strong> {currentApproval?.facility?.name}</p>
                <p><strong>Invoice Type:</strong> LEVY</p>
              </div>
              <div className="col-md-6">
                <p><strong>Period:</strong> {currentApproval?.yearMonth}</p>
                <p><strong>Status:</strong> <span className="badge bg-warning">{currentApproval?.approvalStatus}</span></p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">Comments (Optional)</label>
            <InputTextarea
              className="form-control"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              placeholder="Add any comments about this approval..."
              autoResize
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => {
                setApprovalDialogVisible(false);
                setComments('');
              }}
              disabled={approving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleApproveInvoices}
              disabled={approving}
            >
              {approving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Approving...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-1"></i>
                  Approve & Generate Invoices
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LevyContractReportTab;